import { SimpleForm } from '@/components/ra-forms';
import { TextInput } from '@/components/ra-inputs';
import { Button, Dialog, Divider, List, ListItem, ListItemText, Stack, Toolbar, Typography } from '@mui/material';
import { required, useListContext, useTranslate } from 'ra-core';
import { extractValidSavedQueries, useSavedQueries } from 'ra-ui-materialui';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useGetChipValue } from './FilterSidebarContext';

interface SelectedFiltersItemProps {
  value: unknown;
  source: string;
  lastItem: string;
}

function SelectedFiltersItem(props: SelectedFiltersItemProps): JSX.Element {
  const { value, source, lastItem } = props;
  const label = useGetChipValue({ source, value });

  return (
    <Stack>
      <ListItem alignItems="flex-start" sx={{ pl: 0 }}>
        <ListItemText primary={label} sx={{ my: 0 }} />
      </ListItem>
      {lastItem === source ? null : <Divider />}
    </Stack>
  );
}

function SelectedFiltersList(): JSX.Element {
  const { filterValues } = useListContext();
  const translate = useTranslate();

  return (
    <Stack mt={4} width={'100%'}>
      <Typography variant="h6" fontWeight={'bold'} color={'secondary'}>
        {translate('ra.title.selected_filters')}
      </Typography>
      <List sx={{ bgcolor: 'background.paper', pt: 0, overflowY: 'auto', maxHeight: 400 }}>
        {filterValues
          ? Object.entries(filterValues).map(([key, value]) => {
              const lastEntry = Object.entries(filterValues).pop();
              const lastItem = lastEntry ? lastEntry[0] : '';
              return <SelectedFiltersItem key={key} lastItem={lastItem} value={value} source={key} />;
            })
          : null}
      </List>
    </Stack>
  );
}

interface CustomToolbarProps {
  onClose: () => void;
  addQuery: (queryName: string) => void;
}

function CustomToolbar(props: CustomToolbarProps): JSX.Element {
  const { onClose, addQuery } = props;
  const translate = useTranslate();
  const { handleSubmit } = useFormContext();

  const onFormSubmit = useCallback(
    (data: Record<string, any>) => {
      addQuery(data.queryName);
    },
    [addQuery]
  );

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
      <Button onClick={onClose}>{translate('ra.action.cancel')}</Button>
      <Button variant="contained" onClick={handleSubmit(onFormSubmit)}>
        {translate('ra.action.save')}
      </Button>
    </Toolbar>
  );
}

interface SaveFiltersFormProps {
  open: boolean;
  onClose: () => void;
}

function SaveFiltersDialog(props: SaveFiltersFormProps) {
  const { open, onClose } = props;
  const { filterValues, sort, perPage, displayedFilters, resource } = useListContext();
  const [savedQueries, setSavedQueries] = useSavedQueries(resource);
  const translate = useTranslate();

  const addQuery = useCallback(
    (queryName: string): void => {
      const newSavedQuery = {
        label: queryName.trim(),
        value: {
          filter: filterValues,
          sort,
          perPage,
          displayedFilters
        }
      };
      const newSavedQueries = extractValidSavedQueries(savedQueries);
      setSavedQueries(newSavedQueries.concat(newSavedQuery));
      onClose();
    },
    [filterValues, sort, perPage, displayedFilters, savedQueries, setSavedQueries, onClose]
  );

  const checkAlreadyExists = useCallback(
    () => (value: string) => {
      if (savedQueries.some((query) => query.label === value?.trim())) {
        return 'ra.validation.already_exists';
      }
      return undefined;
    },
    [savedQueries]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <SimpleForm
        toolbar={<CustomToolbar addQuery={addQuery} onClose={onClose} />}
        title={translate('ra.title.save_favorite_filter')}
        modal={true}
      >
        <TextInput
          fullWidth
          source={'queryName'}
          label={translate('ra.input.query_name')}
          placeholder={translate('ra.input.query_name_placeholder')}
          validate={[required(), checkAlreadyExists()]}
        />
        <SelectedFiltersList />
      </SimpleForm>
    </Dialog>
  );
}

export { SaveFiltersDialog };
